import { Image, notification } from "antd";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PostIcon from "../images/post-comment.svg";
import "./CuruChatsPost.css";
import Icons from "./Icons";
import WishListBtn from "./WishListBtn";
import api from "./api";
import FollowButton from "./FollowButton";
import CommentReplies from "./CommentReplies";


//Helper to extract the parent ID to identify a comment
const getParentId = (a) => {
  if (!a) return undefined;
  const answerObjId =
    typeof a.answer === "object" && a.answer ? a.answer.id : undefined;
  const parentObjId =
    typeof a.parent === "object" && a.parent ? a.parent.id : undefined;
  return (
    a.answer_id ??
    a.parent_id ??
    a.reply_to ??
    answerObjId ??
    a.answer ??
    parentObjId ??
    a.parent
  );
};

//Helper to check if an item is a top level comment, meaning it is not a reply
const isTopLevelComment = (a) => {
  const pid = getParentId(a);
  return pid == null || pid === 0 || pid === "0";
};



export default function CuruChatsPost({ post, onProfileClick, onDeleteSuccess }) {
  const [productDetails, setProductDetails] = useState(null);
  const navigate = useNavigate();
  const [likes, setLikes] = useState(post.like_count);
  // const [comments, setComments] = useState(post.answers || []);
  //Below gets only top level comments
  const [comments, setComments] = useState(
    (post.answers || []).filter(isTopLevelComment)
  );


  const [visibleCount, setVisibleCount] = useState(3);
  const [newComment, setNewComment] = useState("");
  const [commentsVisible, setCommentsVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [currentUser, setCurrentUser] = useState(null);
  const [commentLikePending, setCommentLikePending] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);


  const [openReplies, setOpenReplies] = useState({}); 

  const hasProduct = post?.product?.id;

  const goToPage = (id) => {
    navigate(`/product-details/${id}`);
  };

  const fetchProductDetails = useCallback(async () => {
    if (!hasProduct) return;
    try {
      const res = await api.get(`/products/${post.product.id}`);
      setProductDetails(res.data || {});
    } catch (error) {
      console.error("Error fetching tagged product details:", error);
    }
  }, [post.product?.id, hasProduct]);

  useEffect(() => {
    fetchProductDetails();
  }, [fetchProductDetails]);

  const [isLiked, setIsLiked] = useState(false);

  useEffect(() => {
    const fetchLikeStatus = async () => {
      try {
        const res = await api.get(`/comunity/post/${post.id}`);
        if (res.data) {
          setIsLiked(res.data.is_liked || false);
          setLikes(res.data.like_count || 0);
          // setComments(res.data.answers || []);
          //Get list and keep just top level comments
          const allAnswers = res.data.answers || [];
          setComments(allAnswers.filter(isTopLevelComment));
        }
      } catch (error) {
        console.error("Error fetching like status:", error);
      }
    };
    fetchLikeStatus();

    // fetch user for delete permission check
    const fetchUser = async () => {
      try {
        const res = await api.get("/profile");
        setCurrentUser(res.data);
      } catch (err) {
        console.error("Failed to fetch current user", err);
      }
    };
    fetchUser();
  }, [post.id]);

  const handleProfileClick = (user) => {
    if (onProfileClick) {
      onProfileClick(user);
    }
  };

  const handleLikeToggle = async () => {
    try {
      await api.put(`/comunity/post/${post.id}/like`, { like: true });
      setIsLiked((prev) => {
        const toggled = !prev;
        setLikes((l) => l + (toggled ? 1 : -1));
        return toggled;
      });
    } catch (error) {
      console.error("Error toggling like:", error);
    }
  };

  // The processing function for comments and likes
  const handleToggleCommentLikeForAnswer = async (answer) =>{
    const ansId = answer.id;
    const postId = post.id;
  
    // Anti-shake: Return directly if the request is in progress
    if (commentLikePending[ansId]) return;

    const nextIsLiked = !answer.is_liked;
    const delta = nextIsLiked ? 1 : -1;

    setCommentLikePending((prev) => ({...prev, [ansId]: true}));

    setComments((prev) =>
      prev.map((c) =>
        c.id === ansId
          ?{
            ...c,
            is_liked: nextIsLiked,
            like_count: Math.max(0, (c.like_count || 0 ) + delta),
          }
          :c
      )
    );

      try {
        await api.put(`/comunity/post/${postId}/answer/${ansId}/like`,{
          like: nextIsLiked,
        });
      } catch(error){
        console.error("Error toggling comment like:", error);
      
        setComments((prev) =>
          prev.map((c) =>
            c.id ===ansId
              ?{
                ...c,
                is_liked: !nextIsLiked,
                like_count: Math.max(0, (c.like_count || 0) - delta),
              }
              :c
          )
        );
      }finally{
        setCommentLikePending((prev) => {
          const cp = { ...prev };
          delete cp[ansId];
          return cp;
        });
      }
  };

  const handleDeletePost = async () => {
    if (!currentUser || currentUser.id !== post.user?.id) return;
    try {
      await api.delete(`/comunity/post/${post.id}`, { withCredentials: true });
      if (onDeleteSuccess) onDeleteSuccess();
      try {
        notification.success({
          message: "Post deleted",
          description: "Your post was deleted successfully.",
          placement: "topRight",
        });
      } catch (_e) {
        // fallback if notification is unavailable
        console.log("Post deleted successfully");
      }
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401) {
        alert("You must be logged in to delete this post (401).");
      } else if (status === 403) {
        alert("You are not allowed to delete this post (403).");
      } else if (status === 404) {
        alert("Post not found (404).");
      } else {
        console.error("Error deleting post:", error?.response || error);
        alert("Failed to delete post. Please try again.");
      }
    }
  };

  const fetchComments = async () => {
    try {
      const response = await api.get(`/comunity/post/${post.id}`);
      // setComments(response.data.answers || []);
      const allAnswers = response.data.answers || [];
      //Used for debugging purposes
  
    //Filter out replies and just keep top level comments
      setComments(allAnswers.filter(isTopLevelComment));
    } catch (error) {
      console.error("Error fetching comments:", error);
    }
  };

  const handleAddComment = async (message) => {
    if (message.length < 10) {
      setErrorMessage("Comment must be at least 10 characters.");
      return;
    }

    setErrorMessage("");
    try {
      const response = await api.post(`/comunity/post/${post.id}/answer`, {
        message,
      });
      setComments((prev) => [...prev, response.data]);
      setNewComment("");
      await fetchComments();
    } catch (error) {
      console.error("Error adding comment:", error);
    }
  };

  const handleCommentSubmit = async () => {
    if (!newComment.trim()) return;
    await handleAddComment(newComment);
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await api.delete(`/v2/comunity/post/${post.id}/answer/${commentId}`);
      setComments((prev) => prev.filter((c) => c.id !== commentId));
    } catch (error) {
      console.error("Error deleting comment:", error);
    }
  };

  return (
    <div className="chats-post-item card mb-4 border rounded p-3 shadow-sm overflow-hidden d-flex flex-column justify-content-center">
      {/* Post Header */}
      <div className="d-flex align-items-center mb-3 post-header">
        {post.user?.photo ? (
          <img
            src={post.user.photo}
            alt={post.user.username}
            className="chats-post-user-photo rounded-circle me-2"
            onClick={() => handleProfileClick(post.user)}
          />
        ) : (
          <span
            onClick={() => handleProfileClick(post.user)}
            style={{ cursor: "pointer" }}
          >
            <Icons.Profile className="chats-post-user-photo rounded-circle me-2" />
          </span>
        )}

        <div className="ms-3 post-user-details flex-grow-1">
          <div className="username-follow">
            <p
              className="chats-post-username m-0"
              onClick={() => handleProfileClick(post.user)}
            >
              {post.user?.username}
            </p>
            <div className="follow-btn">
              <FollowButton user={post.user} currentUser={currentUser} />
            </div>
          </div>
          <p className="chats-post-user-info mb-0 small">
            {post.category} • {post.created_ago}
          </p>
        </div>

        {currentUser?.id === post.user?.id && (
          <div>
            <button
              className="delete-post-icon-button"
              onClick={() => setShowDeleteConfirm(true)}
              title="Delete post"
              aria-label="Delete post"
            >
              {/* Bin icon */}
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 6h18" stroke="#A3A3A3" strokeWidth="2" strokeLinecap="round"/>
                <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="#A3A3A3" strokeWidth="2" strokeLinecap="round"/>
                <path d="M6 6l1 14a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-14" stroke="#A3A3A3" strokeWidth="2"/>
                <path d="M10 11v6M14 11v6" stroke="#A3A3A3" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Post Body */}
      <div className="chats-post-body d-flex justify-content-between align-items-center mt-3">
        <div className="chats-post-text-and-photos">
          <p className="chats-post-message">{post.message}</p>
          {post.photos?.length > 0 && (
            <div className="chats-post-photos mb-3">
              {post.photos.map((photo) => (
                <Image
                  key={photo.id}
                  src={photo.photo}
                  alt="photo"
                  className="chats-post-photo rounded"
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Product Tag */}
      {hasProduct && (
        <div className="tagged-product-container">
          <div className="chats-product-tag">
            <img
              src={
                post.product.image
                  ? post.product.image
                  : "https://placehold.co/260x360/FFF/c2c2c2?text=No+Picture"
              }
              alt={post.product.name}
              className="chats-product-image"
            />
            <div className="chats-product-info">
              <div className="chats-product-name">{post.product.name}</div>
              <div
                className="chats-product-link"
                onClick={() => goToPage(post.product.id)}
              >
                Go to the product
              </div>
            </div>
            <div className="chats-wishlist-button">
              <WishListBtn
                isAdd={productDetails?.wishlist}
                productId={post.product.id}
                onSuccess={fetchProductDetails}
              />
            </div>
          </div>
        </div>
      )}

      {/* Like & Comment Buttons */}
      <div className="chats-like-comment-buttons me-4 mt-2">
        <button
          className="chats-like-button btn p-0 bg-transparent border-0 d-flex align-items-center me-3"
          onClick={handleLikeToggle}
        >
          <img
            src={`/images/icons/${isLiked ? "heart-filled.svg" : "heart.svg"}`}
            alt="like"
            className="chats-like-icon me-2"
          />
          <span className="post-counts">{likes}</span>
        </button>

        <button
          className="chats-comment-button btn p-0 bg-transparent border-0 d-flex align-items-center"
          onClick={async () => {
            await fetchComments();
            setCommentsVisible(true);
          }}
        >
          <img
            src={`/images/icons/chat.svg`}
            alt="comment"
            className="chats-comment-icon me-2"
          />
          <span className="post-counts">
            {post.answer_count || "No answers"}
          </span>
        </button>
      </div>

      {/* COMMENT INPUT FIELD */}
      <div className="chats-comment-input-container mt-3">
        <input
          type="text"
          className="form-control custom-input mb-2"
          placeholder="Add your answer"
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
        />
        {errorMessage && (
          <div className="text-danger small mb-2">{errorMessage}</div>
        )}
        <button className="post-comment-button" onClick={handleCommentSubmit}>
          <img src={PostIcon} alt="Post Comment" />
        </button>
      </div>

      {/* COMMENT LIST */}
      {commentsVisible && comments.length > 0 && (
        <>
          {comments.slice(0, visibleCount).map((comment) => (
            <div className="comment-container" key={comment.id}>
              <div className="d-flex align-items-center mb-3 post-header">
                {comment.user?.photo ? (
                  <img
                    src={comment.user.photo}
                    alt={comment.user.username}
                    className="chats-post-user-photo rounded-circle me-2"
                    onClick={() => handleProfileClick(comment.user)}
                  />
                ) : (
                  <span
                    onClick={() => handleProfileClick(comment.user)}
                    style={{ cursor: "pointer" }}
                  >
                    <Icons.Profile className="chats-post-user-photo rounded-circle me-2" />
                  </span>
                )}
                <div className="ms-3 post-user-details flex-grow-1">
                  <div className="username-follow">
                    <p
                      className="chats-post-username m-0"
                      onClick={() => handleProfileClick(comment.user)}
                    >
                      {comment.user?.username}
                    </p>
                    <div className="follow-btn">
                      <FollowButton user={comment.user} />
                    </div>
                  </div>
                  <p className="chats-post-user-info mb-0 small">
                    {comment.created_ago}
                  </p>
                </div>
                {/* Comment like button*/}
                <button
                  className="chats-like-button btn p-0 bg-transparent border-0 d-flex align-items-center me-3"
                  onClick={() => handleToggleCommentLikeForAnswer(comment)}
                  disabled={!!commentLikePending?.[comment.id]}  // Prevent connection points
                  aria-pressed={!!comment.is_liked}
                  title={comment.is_liked ? "Cancel Like" : "Like"}
                >
                  <img
                    src={`/images/icons/${comment.is_liked ? "heart-filled.svg" : "heart.svg"}`}
                    alt="like"
                    className="chats-like-icon me-2"
                  />
                  <span className="post-counts">{comment.like_count ?? 0}</span>
                </button>

                {/* Show delete button if it's the user's own comment */}
                {currentUser?.id === comment.user?.id && (
                  <button
                    className="btn btn-sm btn-danger ms-2"
                    onClick={() => handleDeleteComment(comment.id)}
                  >
                    Delete
                  </button>
                )}
                
              </div>



              <div className="chats-post-body d-flex justify-content-between align-items-center mt-3">
                <div className="chats-post-text-and-photos">
                  {/* Comment message */}
                  <p className="chats-post-message mb-2">{comment.message}</p>

                  {/* Reply toggle */}
                <button
                  type="button"
                  className="btn btn-link p-0 small"
                  onClick={() =>
                    setOpenReplies((prev) => ({
                      ...prev,
                      [comment.id]: !prev[comment.id],
                    }))
                  }
                  aria-expanded={!!openReplies[comment.id]}
                  aria-controls={`replies-${comment.id}`}
                >
                  {openReplies[comment.id] ? "Hide replies" : "Reply"}
                </button>

                {/* Replies thread */}
                {openReplies[comment.id] && (
                  <div id={`replies-${comment.id}`} className="mt-2 ms-4">
                    <CommentReplies
                      postId={post.id}
                      parentAnswer={comment}
                      currentUser={currentUser}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
          ))}

          {visibleCount < comments.length && (
            <button
              className="load-comment-button"
              onClick={() => setVisibleCount((prev) => prev + 3)}
            >
              Load more comments
            </button>
          )}
        </>
      )}

      {showDeleteConfirm && (
        <>
          <div className="model-backdrop fade show"></div>
          <div className="delete-confirm-modal">
            <div className="delete-confirm-content">
              <div className="delete-confirm-title">Delete this post?</div>
              <div className="delete-confirm-text">This action cannot be undone.</div>
              <div className="delete-confirm-actions">
                <button className="btn btn-light" onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
                <button
                  className="btn btn-danger"
                  onClick={async () => {
                    setShowDeleteConfirm(false);
                    await handleDeletePost();
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
